import React, { useRef, useState, useEffect, useCallback } from 'react';
import './VirtualJoystick.css';

/**
 * Joystick Virtual para controle de movimento
 * Funciona em desktop e mobile
 */
const VirtualJoystick = ({ onMove }) => {
  const containerRef = useRef(null);
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const currentPos = useRef({ x: 0, y: 0 });

  const MAX_DISTANCE = 40; // Raio máximo do knob

  // Calcular direção normalizada
  const calculateDirection = useCallback((x, y) => {
    const distance = Math.sqrt(x * x + y * y);
    if (distance === 0) return { x: 0, z: 0 };

    // Normalizar e inverter Y (para cima = frente)
    return {
      x: x / MAX_DISTANCE,
      z: -y / MAX_DISTANCE
    };
  }, []);

  // Iniciar drag
  const handleStart = useCallback((clientX, clientY) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    startPos.current = { x: centerX, y: centerY };
    currentPos.current = { x: clientX, y: clientY };
    setIsDragging(true);
  }, []);

  // Mover
  const handleMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;

    let deltaX = clientX - startPos.current.x;
    let deltaY = clientY - startPos.current.y;

    // Limitar ao raio máximo
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance > MAX_DISTANCE) {
      const angle = Math.atan2(deltaY, deltaX);
      deltaX = Math.cos(angle) * MAX_DISTANCE;
      deltaY = Math.sin(angle) * MAX_DISTANCE;
    }

    setPosition({ x: deltaX, y: deltaY });
    currentPos.current = { x: clientX, y: clientY };

    // Callback com direção
    const direction = calculateDirection(deltaX, deltaY);
    onMove(direction);
  }, [isDragging, onMove, calculateDirection]);

  // Finalizar drag
  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
    onMove({ x: 0, z: 0 }); // Parar movimento
  }, [onMove]);

  // Event listeners para mouse
  useEffect(() => {
    const handleMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const handleMouseUp = () => handleEnd();

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Event listeners para touch
  useEffect(() => {
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const handleTouchEnd = () => handleEnd();

    if (isDragging) {
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  return (
    <div
      ref={containerRef}
      className={`virtual-joystick ${isDragging ? 'active' : ''}`}
      onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
      onTouchStart={(e) => {
        if (e.touches.length > 0) {
          handleStart(e.touches[0].clientX, e.touches[0].clientY);
        }
      }}
    >
      {/* Base do joystick */}
      <div className="joystick-base">
        {/* Direções indicativas */}
        <div className="joystick-direction direction-up">▲</div>
        <div className="joystick-direction direction-down">▼</div>
        <div className="joystick-direction direction-left">◀</div>
        <div className="joystick-direction direction-right">▶</div>
      </div>

      {/* Knob (parte que move) */}
      <div
        ref={knobRef}
        className="joystick-knob"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`
        }}
      />
    </div>
  );
};

export default VirtualJoystick;
